import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
  InfoOutlined as InfoIcon,
  FileCopy as ExportIcon,
} from '@mui/icons-material';
import { Alert, CircularProgress, Tooltip } from '@mui/material';
import {GlobalButton,SelectInput,DateInput,AutocompleteInput} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { PICKUP_STATUS_LABELS } from '../config';
import { usePickUpSearchBar, usePickUpsExport } from '../hooks';
import { getPickUpExportValidationMessage } from '../utils/buildPickUpExportBody';
import { PickUpFilters } from '../models';

interface PickUpSearchBarProps {
  search: (filters: PickUpFilters) => void;
  initialFilter: PickUpFilters;
  filter: PickUpFilters;
}

const statusOptions = Object.entries(PICKUP_STATUS_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  })
);

const PickUpSearchBar = ({
  search,
  initialFilter,
  filter,
}: PickUpSearchBarProps) => {
  const {
    localFilter,
    driverInput,
    setDriverInput,
    commerceInput,
    setCommerceInput,
    isDriversLoading,
    isCommercesLoading,
    selectedDriver,
    selectedCommerce,
    driverOptions,
    commerceOptions,
    resetFilter,
    handleSearch,
    handleDriverChange,
    handleCommerceChange,
    handleStatusChange,
    handleCreatedAtFromChange,
    handleCreatedAtToChange,
    handleVisitedAtFromChange,
    handleVisitedAtToChange,
    createdAtFrom,
    createdAtTo,
    visitedAtFrom,
    visitedAtTo,
  } = usePickUpSearchBar({ search, initialFilter, filter });

  const { handleExport, isExporting } = usePickUpsExport();
  const exportValidationMessage = getPickUpExportValidationMessage(localFilter);

  return (
    <div className='w-full flex flex-col justify-center gap-5 p-5 bg-white rounded-lg shadow-md border border-gray-100'>
      <h4 className='text-lg font-black text-gray-800'>Filtros de Búsqueda</h4>
      <div className='flex flex-col gap-5'>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-5'>
          <AutocompleteInput
            name='pickup-driver-search'
            placeholder='Conductor'
            value={selectedDriver}
            inputValue={selectedDriver ? selectedDriver.label : driverInput}
            onChange={handleDriverChange}
            onInputChange={setDriverInput}
            options={driverOptions}
            loading={isDriversLoading}
          />
          <AutocompleteInput
            name='pickup-commerce-search'
            placeholder='Comercio'
            value={selectedCommerce}
            inputValue={
              selectedCommerce ? selectedCommerce.label : commerceInput
            }
            onChange={handleCommerceChange}
            onInputChange={setCommerceInput}
            options={commerceOptions}
            loading={isCommercesLoading}
          />
          <SelectInput
            name='pickup-status-search'
            placeholder='Estado del Retiro'
            value={localFilter.status || ''}
            onChange={handleStatusChange}
            options={statusOptions}
          />
        </div>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-5'>
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-1 text-sm font-semibold text-gray-700'>
              <span>Fecha de creación del retiro</span>
              <Tooltip
                arrow
                title='Fecha en que la visita de retiro fue generada en el sistema. No indica cuándo el conductor retiró los pedidos.'
              >
                <InfoIcon className='cursor-help text-gray-500' fontSize='small' />
              </Tooltip>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <DateInput
                name='created-at-from-search'
                placeholder='Creado desde'
                value={createdAtFrom}
                onChange={handleCreatedAtFromChange}
              />
              <DateInput
                name='created-at-to-search'
                placeholder='Creado hasta'
                value={createdAtTo}
                onChange={handleCreatedAtToChange}
              />
            </div>
          </div>
          <div className='flex flex-col gap-2'>
            <div className='text-sm font-semibold text-gray-700'>
              Fecha efectiva del retiro
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-5'>
              <DateInput
                name='visited-at-from-search'
                placeholder='Retirado desde'
                value={visitedAtFrom}
                onChange={handleVisitedAtFromChange}
              />
              <DateInput
                name='visited-at-to-search'
                placeholder='Retirado hasta'
                value={visitedAtTo}
                onChange={handleVisitedAtToChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='flex gap-3 justify-end mt-2'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={resetFilter}
          disabled={isExporting}
        >
          <TrashIcon color='primary' className='mr-1' />
          Limpiar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => handleExport(localFilter)}
          disabled={isExporting || !!exportValidationMessage}
        >
          {isExporting ? (
            <CircularProgress size={20} color='inherit' className='mr-1' />
          ) : (
            <ExportIcon color='primary' className='mr-1' />
          )}
          Exportar
        </GlobalButton>
        <GlobalButton
          variant={GlobalButtonVariant.primaryButton}
          action={handleSearch}
          disabled={isExporting}
        >
          <SearchIcon sx={{ color: 'white' }} className='mr-1' />
          Buscar
        </GlobalButton>
      </div>
      {exportValidationMessage ? (
        <Alert severity='warning'>{exportValidationMessage}</Alert>
      ) : null}
    </div>
  );
};

export default PickUpSearchBar;
